/**
 * Vuex telemetry plugin.
 *
 * Instruments a curated set of async data-fetching actions with OTel spans.
 * Only actions in TRACKED_ACTIONS are reported — UI-toggle and write actions
 * are excluded to avoid span noise.
 *
 * Each tracked action emits:
 *   - A span covering the full async duration (before → after/error)
 *   - A "vuex:actionDuplicate" event when the same action is already in-flight
 *   - A "vuex:actionTimeout" event when an action has not resolved after 30 s
 *
 * Navigation settle:
 *   When all in-flight tracked actions complete after a navigation started,
 *   settleNavigation() is called to record the "navigation:settled" metric.
 *   This replaces the PerformanceTracker's Axios interceptor approach with
 *   action-level accuracy.
 *
 * Add action names here as new data-fetching actions are identified.
 */

import Instrumentation from "@/common/instrumentation";
import { getNavigationState } from "@/common/instrumentation";

const TRACKED_ACTIONS = new Set([
  // Auth / user
  "checkAuth",
  "refreshCurrentUser",
  "fetchCurrentUserSettings",
  "fetchTeamInvitations",
  "fetchCustomReportList",

  // Projects / jobs
  "fetchCurrentProject",
  "fetchCurrentProjectAtsStagesForJob",

  // Candidates
  "fetchCurrentCandidate",
  "fetchCandidateFromHumanly",
  "fetchConversationTranscript",
  "fetchCandidateDedupeMessage",
  "fetchCandidateConnectors",
  "fetchTagOptions",

  // Campaigns
  "fetchCampaign",
  "fetchCampaignForCopy",
  "fetchNewCampaign",

  // Sourcing / review
  "fetchSearchFilterOptions",
  "fetchReviewDataForProject",
  "fetchReviewData",
  "fetchReviewCount",

  // AI generation
  "generateNewCampaign",
  "generateNewCampaignForInAppSourcing",
  "generateNewFirstMessage",
  "generateNewFollowUp",
  "regenerateAllMessages",
]);

const TIMEOUT_MS = 30000;

export default function telemetryPlugin(store) {
  // in-flight: action type → { span, timeoutId, count }
  const in_flight = new Map();

  function checkNavigationSettle() {
    if (in_flight.size === 0 && getNavigationState()) {
      Instrumentation.settleNavigation();
    }
  }

  store.subscribeAction({
    before(action) {
      if (!TRACKED_ACTIONS.has(action.type)) return;

      if (in_flight.has(action.type)) {
        const entry = in_flight.get(action.type);
        entry.count += 1;
        Instrumentation.addEvent("vuex:actionDuplicate", {
          "action.type": action.type,
          "action.concurrentCount": entry.count,
        });
        return;
      }

      const span = Instrumentation.startSpan(`vuex:${action.type}`);
      const timeout_id = setTimeout(() => {
        Instrumentation.addEvent("vuex:actionTimeout", {
          "action.type": action.type,
          "action.timeoutMs": TIMEOUT_MS,
        });
      }, TIMEOUT_MS);

      in_flight.set(action.type, { span, timeout_id, count: 1 });
    },

    after(action) {
      if (!TRACKED_ACTIONS.has(action.type)) return;
      const entry = in_flight.get(action.type);
      if (!entry) return;
      clearTimeout(entry.timeout_id);
      in_flight.delete(action.type);
      Instrumentation.endSpan(entry.span);
      checkNavigationSettle();
    },

    error(action, error) {
      if (!TRACKED_ACTIONS.has(action.type)) return;
      const entry = in_flight.get(action.type);
      if (!entry) return;
      clearTimeout(entry.timeout_id);
      in_flight.delete(action.type);
      Instrumentation.endSpan(entry.span, error);
      checkNavigationSettle();
    },
  });
}
